import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X } from "lucide-react";
import siteLogo from "../assets/images/site-logo.svg";
import { getLatestBlogSlug } from "./Blog/helper";
import {
  getLatestChallengeSlug,
  getLatestLOTWSlug,
} from "./weeklyChallenges/customChallenge";
import { BlogCategoryType } from "../types/blog";
import { getBlogCategories } from "../services/firebase";

interface NavbarProps {
  className?: string;
}

const Navbar: React.FC<NavbarProps> = ({ className = "" }) => {
  const { user, logout, userRole } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [latestSlug, setLatestSlug] = useState({
    blog: "",
    challenge: "",
    lotw: "",
  });
  const [blogCategories, setBlogCategories] = useState<BlogCategoryType[]>([]);
  const [selectedBlogCategory, setSelectedBlogCategory] = useState<
    number | undefined
  >();
  const location = useLocation();
  const userName = user?.email ? user.email.split("@")[0] : "User";
  // Logout handler
  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    // On mobile, close the menu after clicking
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  // Custom link component that scrolls to top
  const NavLink = ({
    to,
    children,
    ...props
  }: {
    to: string;
    children: React.ReactNode;
    className?: string;
    target?: string;
  }) => (
    <Link to={to} onClick={scrollToTop} {...props}>
      {children}
    </Link>
  );

  useEffect(() => {
    async function fetchSlug() {
      try {
        const blogSlug = await getLatestBlogSlug(selectedBlogCategory);
        const challengeSlug = await getLatestChallengeSlug();
        const lotwSlug = await getLatestLOTWSlug();
        setLatestSlug({
          blog: blogSlug || "",
          challenge: challengeSlug || "",
          lotw: lotwSlug || "",
        });
      } catch (err) {
        console.error("Failed to fetch slug:", err);
      }
    }
    fetchSlug();
    fetchBlogCategories();
  }, []);

  const fetchBlogCategories = async () => {
    try {
      const blogCategoryList = await getBlogCategories();
      //sorting as per display order
      blogCategoryList.sort(
        (a, b) => a.blog_category_display_order - b.blog_category_display_order
      );
      setBlogCategories(blogCategoryList);
    } catch (error) {
      console.error("Failed to fetch blog categories:", error);
    }
  };

  const NavItems = React.memo(() => (
    <>
      {user ? (
        <>
          <ul className={`${isMenuOpen ? "active" : ""}`}>
            <li
              className={
                location.pathname === "/punishments"
                  ? "active haveSubMenu"
                  : "haveSubMenu"
              }
            >
              <span>Punishments</span>
              <ul>
                <li
                  className={
                    location.pathname === "/punishments" ? "active" : ""
                  }
                >
                  <NavLink to="/punishments">Punishment Gallery</NavLink>
                </li>
                {/* <li>
                  <NavLink target="_blank" to="#">Top 100 Punishments</NavLink>
                </li> */}
                <li
                  className={
                    location.pathname ===
                    "https://h4hcbpgn1q9.typeform.com/to/E21Gfvu6"
                      ? "active"
                      : ""
                  }
                >
                  <NavLink
                    target="_blank"
                    to="https://h4hcbpgn1q9.typeform.com/to/E21Gfvu6"
                  >
                    Submit a Punishment
                  </NavLink>
                </li>
              </ul>
            </li>
            <li
              className={
                location.pathname === "/draft-ideas"
                  ? "active haveSubMenu"
                  : "haveSubMenu"
              }
            >
              <span>Draft Ideas</span>
              <ul>
                <li
                  className={
                    location.pathname === "/draft-ideas" ? "active" : ""
                  }
                >
                  <NavLink to="/draft-ideas">Draft Ideas Gallery</NavLink>
                </li>
                <li>
                  <NavLink
                    target="_blank"
                    to="https://h4hcbpgn1q9.typeform.com/to/uZSLaOq7"
                  >
                    Submit a Draft Idea
                  </NavLink>
                </li>
              </ul>
            </li>
            <li
              className={
                location.pathname === `/challenges/${latestSlug.challenge}`
                  ? "active"
                  : ""
              }
            >
              <NavLink to={`/challenges/${latestSlug.challenge}`}>
                Challenges
              </NavLink>
            </li>
            {
              <li
                className={
                  location.pathname === `/lotw/${latestSlug.lotw}`
                    ? "active"
                    : ""
                }
              >
                <NavLink to={`/lotw/${latestSlug.lotw}`}>
                  Loser of the Week
                </NavLink>
              </li>
            }
            <li
              className={
                location.pathname.includes("articles")
                  ? "active haveSubMenu"
                  : "haveSubMenu"
              }
            >
              <span>Articles</span>
              <ul>
                <li
                  className={location.pathname === `/articles` ? "active" : ""}
                >
                  <NavLink to="/articles">All</NavLink>
                </li>
                {blogCategories.map((blogCategory) => (
                  <li
                    key={blogCategory.id}
                    onClick={() =>
                      setSelectedBlogCategory(blogCategory.blog_category_id)
                    }
                    className={
                      location.pathname.includes(
                        `/articles/${blogCategory.blog_category_id}`
                      )
                        ? "active"
                        : ""
                    }
                  >
                    <NavLink
                      to={`/articles/${blogCategory.blog_category_id}/${latestSlug.blog}`}
                    >
                      {blogCategory.blog_category_name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
            <li
              className={
                location.pathname === "/https://shop.fantasyloser.com"
                  ? "active"
                  : ""
              }
            >
              <NavLink target="_blank" to="https://shop.fantasyloser.com">
                Shop
              </NavLink>
            </li>
            <li className="rightItem">
              <NavLink to="" className="adminIcon">
                Admin
              </NavLink>
              <ul>
                {userRole === "admin" && (
                  <li
                    className={location.pathname === "/admin" ? "active" : ""}
                  >
                    <NavLink to="/admin">Admin</NavLink>
                  </li>
                )}
                {userRole === "user" && (
                  <li
                    className={
                      location.pathname === "/dashboard" ? "active" : ""
                    }
                  >
                    <NavLink to="/dashboard">Dashboard</NavLink>
                  </li>
                )}
                <li>
                  <button onClick={handleLogout} className="headerUser">
                    Logout{" "}
                    {window.location.hostname === "fantasyloser.com"
                      ? ""
                      : userName}
                  </button>
                </li>
              </ul>
            </li>
          </ul>
          <span
            className={`${isMenuOpen ? "menuIcon active" : "menuIcon"}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            Menu Icon
          </span>
        </>
      ) : (
        <>
          <ul className={`${isMenuOpen ? "active" : ""}`}>
            <li
              className={
                location.pathname === "/punishments"
                  ? "active haveSubMenu"
                  : "haveSubMenu"
              }
            >
              <span>Punishments</span>
              <ul>
                <li
                  className={
                    location.pathname === "/punishments" ? "active" : ""
                  }
                >
                  <NavLink to="/punishments">Punishment Gallery</NavLink>
                </li>
                {/* <li>
                  <NavLink target="_blank" to="#">Top 100 Punishments</NavLink>
                </li> */}
                <li
                  className={
                    location.pathname ===
                    "/https://h4hcbpgn1q9.typeform.com/to/E21Gfvu6"
                      ? "active"
                      : ""
                  }
                >
                  <NavLink
                    target="_blank"
                    to="https://h4hcbpgn1q9.typeform.com/to/E21Gfvu6"
                  >
                    Submit a Punishment
                  </NavLink>
                </li>
              </ul>
            </li>
            <li
              className={
                location.pathname === "/draft-ideas"
                  ? "active haveSubMenu"
                  : "haveSubMenu"
              }
            >
              <span>Draft Ideas</span>
              <ul>
                <li
                  className={
                    location.pathname === "/draft-ideas" ? "active" : ""
                  }
                >
                  <NavLink to="/draft-ideas">Draft Ideas Gallery</NavLink>
                </li>
                <li>
                  <NavLink
                    target="_blank"
                    to="https://h4hcbpgn1q9.typeform.com/to/uZSLaOq7"
                  >
                    Submit a Draft Idea
                  </NavLink>
                </li>
              </ul>
            </li>
            <li
              className={
                location.pathname === `/challenges/${latestSlug.challenge}`
                  ? "active"
                  : ""
              }
            >
              <NavLink to={`/challenges/${latestSlug.challenge}`}>
                Challenges
              </NavLink>
            </li>
            {
              <li
                className={
                  location.pathname === `/lotw/${latestSlug.lotw}`
                    ? "active"
                    : ""
                }
              >
                <NavLink to={`/lotw/${latestSlug.lotw}`}>
                  Loser of the Week
                </NavLink>
              </li>
            }
            <li
              className={
                location.pathname.includes("articles")
                  ? "active haveSubMenu"
                  : "haveSubMenu"
              }
            >
              <span>Articles</span>
              <ul>
                <li
                  className={location.pathname === `/articles` ? "active" : ""}
                >
                  <NavLink to="/articles">All</NavLink>
                </li>
                {blogCategories.map((blogCategory) => (
                  <li
                    key={blogCategory.id}
                    onClick={() =>
                      setSelectedBlogCategory(blogCategory.blog_category_id)
                    }
                    className={
                      location.pathname.includes(
                        String(blogCategory.blog_category_id)
                      )
                        ? "active"
                        : ""
                    }
                  >
                    <NavLink
                      to={`/articles/${blogCategory.blog_category_id}/${latestSlug.blog}`}
                    >
                      {blogCategory.blog_category_name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
            <li
              className={
                location.pathname === "/https://shop.fantasyloser.com"
                  ? "active"
                  : ""
              }
            >
              <NavLink target="_blank" to="https://shop.fantasyloser.com">
                Shop
              </NavLink>
            </li>
            <li className={location.pathname === "/login" ? "signInBtn active" : "signInBtn"}>
              <NavLink to="/home">Sign In</NavLink>
            </li>
            {/* <li className="rightItem">
              <NavLink to="" className="adminIcon">
                Login
              </NavLink>
              <ul>
              </ul>
            </li> */}
          </ul>
          <span
            className={`${isMenuOpen ? "menuIcon active" : "menuIcon"}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            Menu Icon
          </span>
        </>
      )}
    </>
  ));

  return (
    <div className="topHeader">
      <div className="wrapper">
        <Link to={user ? "/" : "/"} className="siteLogo" onClick={scrollToTop}>
          <img src={siteLogo} alt="FL Logo" />
        </Link>
        <nav className="topNav">
          <NavItems />
        </nav>
      </div>
    </div>
  );
};

export default React.memo(Navbar);
